import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';

const DEFAULT_BUY_THRESHOLD = 0.5;
const DEFAULT_SELL_THRESHOLD = -0.6;

export interface StrategySettingsData {
    targetProfitPercent: number;
    stopLossPercent: number;
    enableTakeProfit: boolean;
    enableStopLoss: boolean;
    buyThreshold: number;
    sellThreshold: number;
}

export const useStrategySettings = () => {
    const [settings, setSettings] = useState<StrategySettingsData>({
        targetProfitPercent: 20,
        stopLossPercent: 10,
        enableTakeProfit: true,
        enableStopLoss: true,
        buyThreshold: DEFAULT_BUY_THRESHOLD,
        sellThreshold: DEFAULT_SELL_THRESHOLD,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await apiFetch(`${API_BASE_URL}/api/settings/strategy`);

            if (!response.ok) {
                throw new Error(`설정 로드 실패: ${response.status}`);
            }

            const data = await response.json();

            setSettings({
                targetProfitPercent: data.target_profit_percent,
                stopLossPercent: data.stop_loss_percent,
                enableTakeProfit: data.enable_take_profit,
                enableStopLoss: data.enable_stop_loss,
                buyThreshold: data.buy_threshold ?? DEFAULT_BUY_THRESHOLD,
                sellThreshold: data.sell_threshold ?? DEFAULT_SELL_THRESHOLD,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '설정 로드 중 오류 발생');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const saveSettings = async (newSettings: StrategySettingsData) => {
        try {
            setSaving(true);
            setError(null);
            setSuccessMessage(null);

            const response = await apiFetch(`${API_BASE_URL}/api/settings/strategy`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    target_profit_percent: newSettings.targetProfitPercent,
                    stop_loss_percent: newSettings.stopLossPercent,
                    enable_take_profit: newSettings.enableTakeProfit,
                    enable_stop_loss: newSettings.enableStopLoss,
                    buy_threshold: newSettings.buyThreshold,
                    sell_threshold: newSettings.sellThreshold,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `설정 저장 실패: ${response.status}`);
            }

            setSettings(newSettings);
            setSuccessMessage('설정이 저장되었습니다');

            setTimeout(() => {
                setSuccessMessage(null);
            }, 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : '설정 저장 중 오류 발생');
        } finally {
            setSaving(false);
        }
    };

    return {
        settings,
        loading,
        saving,
        error,
        successMessage,
        loadSettings,
        saveSettings,
        setSettings, // For local state updates in form
    };
};
